// Driver API client for fetching race data, attaches JWT from localStorage

import { DriverRaceDataResponse } from '../models/DriverRaceDataResponse';
import { MatchDriverResponse } from '../models/MatchDriverResponse';
import { PagedResponse } from '../models/PagedResponse';
import { FindDriverRequestBody } from '../models/requestBodies/FindDriverRequestBody';
import { type Membership } from '../models/Membership';
import { DriverUsedFacility } from '../models/RaceData';
import { FilterCommonResponse } from '../models/FilterCommonResponse';
import { CareerProgress } from '../models/CareerProgress';
import { LeaderboardWithResults } from '../models/LeaderboardWithResults';
import { LeaderboardWithRecentResults } from '../models/LeaderboardWithRecentResults';
import { RaceAroundWorldProgress } from '../models/RaceAroundWorldProgress';
import { RaceLap, TelemetryLapData } from '../models/TelemetryData';
import { AnalysisRequestEntry } from '../models/requestBodies/AnalysisRequestBody';
import { AnalysisSubmitResponse, AnalysisJobResponse } from '../models/responseBodies/AnalysisResponse';
import { ReferenceLapsResponse } from '../models/responseBodies/ReferenceLapsResponse';
import { DriverReferenceSearchResult } from '../models/responseBodies/DriverReferenceSearchResult';
import { BaseClient } from './BaseClient';
import { currentTenant } from '../tenant/tenants';

export interface GetUserRacesParams {
  take: number;
  skip: number;
  car?: string;
  track?: string;
}

export interface GetFitleredUserRacesParams {
  take: number;
  skip: number;
  car: string;
  track: string;
  trackLayout: string;
}

/**
 * `POST driver/link` on a venue API. `linked`: the venue driver the account is now (or already was)
 * linked to. `no-match`: no unlinked driver at the venue has the account's email. `no-email`: the
 * login token carries no email to match on. Either refusal means staff link it at the front desk.
 */
export type LinkDriverResult =
  | { status: 'linked'; driver: MatchDriverResponse }
  | { status: 'no-match' }
  | { status: 'no-email' };

// The brand's race-data API: CommonApi for World of Racing, the venue's own WebApi for the others.
const getDriverApiBaseUrl = (): string => {
  const explicit = currentTenant.raceDataApi;
  if (!explicit) {
    throw new Error(`Missing race data API base URL for tenant '${currentTenant.key}'`);
  }
  return explicit.endsWith('/') ? explicit : explicit + '/';
};

class DriverClient extends BaseClient {
  private baseUrl: string;

  constructor() {
    super();
    this.baseUrl = getDriverApiBaseUrl();
  }

  // GET {base}/racedata/user/races?Take=..&Skip=..&DriverId=..
  public async getUserRaces(params: GetUserRacesParams): Promise<PagedResponse<DriverRaceDataResponse>> {
    const { take, skip, car, track } = params;
    const search = new URLSearchParams({
      Take: String(take),
      Skip: String(skip),
    });

    if (car) {
      search.append('Car', car);
    }
    if (track) {
      search.append('Track', track);
    }

    const url = `${this.baseUrl}racedata/user/races?${search.toString()}`;

    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (!response.ok) {
      throw new Error(`Failed to load user races (${response.status})`);
    }
    const payload = (await response.json()) as PagedResponse<DriverRaceDataResponse> | DriverRaceDataResponse[];

    if (Array.isArray(payload)) {
      return { data: payload, totalCount: payload.length };
    }
    return payload;
  }

  public async getFilterCars(track: string | undefined): Promise<FilterCommonResponse[]> {
    let url = `${this.baseUrl}racedata/user/filter/cars`;

    if (track) {
      url += `?withTrack=${track}`;
    }

    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (!response.ok) {
      throw new Error(`Failed to load car filters (${response.status})`);
    }
    return await response.json();
  }

  public async getFilterTracks(car: string | undefined): Promise<FilterCommonResponse[]> {
    let url = `${this.baseUrl}racedata/user/filter/tracks`;
    if (car) {
      url += `?withCar=${car}`;
    }

    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (!response.ok) {
      throw new Error(`Failed to load track filters (${response.status})`);
    }
    return await response.json();
  }

  public async findDriverByUserData(userData: FindDriverRequestBody): Promise<MatchDriverResponse | null> {
    const search = new URLSearchParams({
      email: userData.email,
      firstName: userData.firstName,
      lastName: userData.lastName
    });
    const url = `${this.baseUrl}driver/find?${search.toString()}`;

    const response = await this.fetchWithAuth(url, { method: 'GET' });

    if (!response.ok) {
      throw new Error(`Failed to find driver by user data (${response.status})`);
    }

    const text = await response.text();
    if (!text) {
      return null;
    }

    return (JSON.parse(text)) as MatchDriverResponse | null;
  }

  /** GET {venue}/driver/me: the venue driver linked to the caller, or null (404) when there is none. */
  public async getMyDriver(): Promise<MatchDriverResponse | null> {
    const response = await this.fetchWithAuth(`${this.baseUrl}driver/me`, { method: 'GET' });
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error(`Failed to load your driver (${response.status})`);
    }
    return (await response.json()) as MatchDriverResponse;
  }

  /** POST {venue}/driver/link: claim the venue driver whose email is the account's. */
  public async linkMyDriver(): Promise<LinkDriverResult> {
    const response = await this.fetchWithAuth(`${this.baseUrl}driver/link`, { method: 'POST' });
    if (response.ok) {
      return { status: 'linked', driver: (await response.json()) as MatchDriverResponse };
    }
    if (response.status === 404) {
      return { status: 'no-match' };
    }
    if (response.status === 400) {
      return { status: 'no-email' };
    }
    throw new Error(`Failed to link your driver (${response.status})`);
  }

  // GET {base}/membership/{driverId}
  public async getMembershipByDriverId(driverId: string): Promise<Membership | null> {
    if (!driverId) {
      throw new Error('driverId is required');
    }

    const url = `${this.baseUrl}driver/membership`;

    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error(`Failed to load membership (${response.status})`);
    }
    return (await response.json()) as Membership | null;
  }

  public async getDriverUsedFacilities(driverId: string): Promise<DriverUsedFacility[] | null> {
    if (!driverId) {
      throw new Error('driverId is required');
    }

    const url = `${this.baseUrl}racedata/facilities`;

    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error(`Failed to load used facilities (${response.status})`);
    }
    return (await response.json()) as DriverUsedFacility[] | null;
  }

  public async getDriverLeaderboard(params: GetFitleredUserRacesParams): Promise<DriverRaceDataResponse[] | null> {
    const { take, skip, car, track, trackLayout } = params;
    const search = new URLSearchParams({
      Take: String(take),
      Skip: String(skip),
      Car: String(car),
      Track: String(track),
      TrackLayout: String(trackLayout)
    });

    const url = `${this.baseUrl}racedata/leaderboard?${search.toString()}`;

    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (!response.ok) {
      throw new Error(`Failed to load user races (${response.status})`);
    }
    const payload = (await response.json()) as DriverRaceDataResponse[] | null;

    return payload;
  }

  public async getLeaderboardById(id: string, days?: string | null, count?: string | null): Promise<LeaderboardWithResults> {
    const search = new URLSearchParams();
    if (days) search.append('days', days);
    if (count) search.append('count', count);
    const query = search.toString();

    const url = `${this.baseUrl}racedata/top/boards/${id}${query ? `?${query}` : ''}`;

    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (!response.ok) {
      throw new Error(`Failed to load leaderboard ${id} (${response.status})`);
    }
    return (await response.json()) as LeaderboardWithResults;
  }

  public async getRecentResultsById(id: string, count?: string | null): Promise<LeaderboardWithRecentResults> {
    const search = new URLSearchParams();
    if (count) search.append('count', count);
    const query = search.toString();

    const url = `${this.baseUrl}racedata/latest/boards/${id}${query ? `?${query}` : ''}`;

    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (!response.ok) {
      throw new Error(`Failed to load recent results ${id} (${response.status})`);
    }
    return (await response.json()) as LeaderboardWithRecentResults;
  }

  public async getDriverCareerProgress(driverId: string): Promise<CareerProgress[] | null> {
    if (!driverId) {
      throw new Error('driverId is required');
    }

    const url = `${this.baseUrl}racedata/career`;

    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error(`Failed to load career progress (${response.status})`);
    }
    return (await response.json()) as CareerProgress[] | null;
  }

  public async getRaceAroundWorldProgress(driverId: string): Promise<RaceAroundWorldProgress[] | null> {
    if (!driverId) {
      throw new Error('driverId is required');
    }

    const url = `${this.baseUrl}driver/around-the-world`;

    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error(`Failed to load Race Around the World progress (${response.status})`);
    }
    return (await response.json()) as RaceAroundWorldProgress[] | null;
  }

  public async getLapsForLap(lapId: string): Promise<RaceLap[]> {
    if (!lapId) {
      throw new Error('lapId is required');
    }

    const url = `${this.baseUrl}racedata/lap/${lapId}/laps`;

    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (!response.ok) {
      throw new Error(`Failed to load race laps (${response.status})`);
    }
    return (await response.json()) as RaceLap[];
  }

  public async getReferenceLaps(track: string, car: string, layout?: string): Promise<ReferenceLapsResponse> {
    if (!track || !car) {
      throw new Error('track and car are required');
    }

    // Send layout whenever it is supplied — including an empty string, which is a
    // real Assetto layout key (the base layout). Only `undefined` means "no layout
    // filter"; collapsing "" to no-filter would search across all layouts.
    const search = layout !== undefined ? `?layout=${encodeURIComponent(layout)}` : '';
    const url = `${this.baseUrl}racedata/reference/${encodeURIComponent(track)}/${encodeURIComponent(car)}${search}`;

    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (!response.ok) {
      throw new Error(`Failed to load reference laps (${response.status})`);
    }
    return (await response.json()) as ReferenceLapsResponse;
  }

  public async searchReferenceDrivers(
    track: string,
    car: string,
    layout: string | undefined,
    q: string
  ): Promise<DriverReferenceSearchResult[]> {
    if (!track || !car) {
      throw new Error('track and car are required');
    }
    const params = new URLSearchParams({ track, car, q });
    // Supplied empty string is the base layout, not "no layout" — send it so the
    // search is scoped to that layout. Only `undefined` omits the filter.
    if (layout !== undefined) params.set('layout', layout);
    const url = `${this.baseUrl}racedata/reference/drivers?${params.toString()}`;

    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (!response.ok) {
      throw new Error(`Failed to search drivers (${response.status})`);
    }
    return (await response.json()) as DriverReferenceSearchResult[];
  }

  public async getLapTelemetry(lapId: string, location?: string): Promise<TelemetryLapData> {
    if (!lapId) {
      throw new Error('lapId is required');
    }

    const search = location ? `?location=${encodeURIComponent(location)}` : '';
    const url = `${this.baseUrl}racedata/telemetry/lap/${lapId}${search}`;

    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (!response.ok) {
      throw new Error(`Failed to load lap telemetry (${response.status})`);
    }
    return (await response.json()) as TelemetryLapData;
  }

  public async submitAnalysis(
    entries: AnalysisRequestEntry[],
    signal?: AbortSignal
  ): Promise<AnalysisSubmitResponse> {
    if (entries.length === 0) {
      throw new Error('At least one analysis entry is required');
    }

    const url = `${this.baseUrl}racedata/analysis`;

    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(entries),
      signal,
    });

    if (!response.ok) {
      if (response.status === 429) {
        throw new Error('Rate limited — try again shortly.');
      }
      throw new Error('Analysis failed — try again.');
    }

    return (await response.json()) as AnalysisSubmitResponse;
  }

  public async getAnalysisResult(
    jobId: string,
    signal?: AbortSignal
  ): Promise<AnalysisJobResponse> {
    const url = `${this.baseUrl}racedata/analysis/${jobId}`;

    const response = await this.fetchWithAuth(url, {
      method: 'GET',
      signal,
    });

    if (response.status === 404) {
      throw new Error('Analysis job not found or expired.');
    }

    if (!response.ok) {
      throw new Error('Analysis failed — try again.');
    }

    return (await response.json()) as AnalysisJobResponse;
  }

  //bookings section

  public async getBookingLocations(): Promise<string[]> {
    const url = `${this.baseUrl}locations`;
    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (!response.ok) {
      throw new Error(`Failed to load available locations (${response.status})`);
    }
    return (await response.json()) as string[];
  }
}


const driverApi = new DriverClient();
export default driverApi;


